import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as dropboxService from '../../../services/dropbox';
import {
  setFiles,
  addFile,
  updateFile,
  removeFile,
  setLoading,
  setError,
} from '../../../store/reducers/dropbox';

/**
 * Custom hook for file operations
 */
export const useDropboxFiles = () => {
  const dispatch = useDispatch();
  const [loading, setLocalLoading] = useState(false);

  // Fetch files
  const fetchFiles = useCallback(
    async (params?: {
      folder_id?: string;
      include_deleted?: boolean;
      starred_only?: boolean;
      global_only?: boolean;
      personal_only?: boolean;
    }) => {
      try {
        dispatch(setLoading(true));
        const response = await dropboxService.getFiles(params);

        if (response?.success) {
          dispatch(setFiles(response.data));
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to fetch files';
          dispatch(setError(errorMsg));
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while fetching files';
        dispatch(setError(errorMsg));
        toast.error(errorMsg);
        return null;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  // Rename file
  const renameFile = useCallback(
    async (fileId: string, newName: string) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.updateFile(fileId, { name: newName });

        if (response?.success) {
          dispatch(updateFile(response.data));
          toast.success('File renamed successfully');
          return response.data;
        } else {
          const errorMsg = response?.errors[0]?.msg || response?.message || 'Failed to rename file';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while renaming file';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Delete file
  const deleteFile = useCallback(
    async (fileId: string, permanent: boolean = false) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.deleteFile(fileId, permanent);

        if (response?.success) {
          if (permanent) {
            dispatch(removeFile(fileId));
            toast.success('File permanently deleted');
          } else {
            dispatch(updateFile(response.data));
            toast.success('File moved to trash');
          }
          return true;
        } else {
          const errorMsg = response?.message || 'Failed to delete file';
          toast.error(errorMsg);
          return false;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while deleting file';
        toast.error(errorMsg);
        return false;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Restore file
  const restoreFile = useCallback(
    async (fileId: string) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.restoreFile(fileId);

        if (response?.success) {
          dispatch(updateFile(response.data));
          toast.success('File restored successfully');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to restore file';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while restoring file';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Download file
  const downloadFile = useCallback(async (fileId: string, fileName: string, version?: number) => {
    try {
      setLocalLoading(true);
      const response = await dropboxService.downloadFile(fileId, version);

      if (response?.success && response?.data?.downloadUrl) {
        // Open download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
        toast.success('Download started');
        
        // Track file access
        await dropboxService.trackFileAccess(fileId, 'downloaded');
        
        return true;
      } else {
        const errorMsg = response?.message || 'Failed to download file';
        toast.error(errorMsg);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An error occurred while downloading file';
      toast.error(errorMsg);
      return false;
    } finally {
      setLocalLoading(false);
    }
  }, []);

  // Star/unstar file
  const toggleStar = useCallback(
    async (fileId: string, isStarred: boolean) => {
      try {
        const response = isStarred
          ? await dropboxService.unstarFile(fileId)
          : await dropboxService.starFile(fileId);

        if (response?.success) {
          dispatch(updateFile(response.data));
          toast.success(isStarred ? 'Removed from favorites' : 'Added to favorites');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to update favorite status';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while updating favorite status';
        toast.error(errorMsg);
        return null;
      }
    },
    [dispatch]
  );

  // Move file
  const moveFileTo = useCallback(
    async (fileId: string, newFolderId: string | null) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.moveFile(fileId, newFolderId);

        if (response?.success) {
          dispatch(updateFile(response.data));
          toast.success('File moved successfully');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to move file';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while moving file';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Reorder file
  const reorderFile = useCallback(
    async (fileId: string, sortOrder: number) => {
      try {
        const response = await dropboxService.reorderFile(fileId, sortOrder);

        if (response?.success) {
          dispatch(updateFile(response.data));
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to reorder file';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while reordering file';
        toast.error(errorMsg);
        return null;
      }
    },
    [dispatch]
  );

  return {
    loading,
    fetchFiles,
    renameFile,
    deleteFile,
    restoreFile,
    downloadFile,
    toggleStar,
    moveFileTo,
    reorderFile,
  };
};
